import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { Box, Button, CircularProgress, Skeleton, Tooltip, Autocomplete, TextField, } from "@mui/material";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import PushPinIcon from "@mui/icons-material/PushPin";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { globalGetService, globalPostService, } from "../../../utils/globalApiServices";
import { getGraphOptionsApi, getMaintenanceChecksApi, pinGraphApi, unpinGraphApi, extractMaintenanceEvents } from "../apiService";
import { checkApiStatus } from "../../../utils";
import { exportToExcel } from "../../../utils/helper";
import { useSnackbar } from "notistack";
import { trackActivity } from "../../../utils/mixpanel";
import { FilterComponent, hanwahaInstance } from "../../../shared_elements";
import { parseGraphFilterKeys, pickFilterMenuForKeys, cleanFilterQuery, } from "../analyticsFilterMenu";
import { assetType as assetTypeNameById } from "../../../constants";
import GraphTable from "./GraphTable";
import { useInvoiceBreakdown, InvoiceBreakdownModal } from "./InvoiceBreakdown";
import { useLeaseExpiryBreakdown, LeaseExpiryBreakdownModal } from "./LeaseExpiryBreakdown";
import { usePaymentDelayBreakdown, PaymentDelayBreakdownModal } from "./PaymentDelayBreakdown";
import { useOverdueInvoiceConcentrationBreakdown, OverdueInvoiceConcentrationModal } from "./OverdueInvoiceConcentrationBreakdown";
import { useObligationStatusBreakdown, ObligationStatusBreakdownModal } from "./ObligationStatusBreakdown";
import { useInvoiceVolumeValueByTypeBreakdown, InvoiceVolumeValueByTypeBreakdownModal } from "./InvoiceVolumeValueByTypeBreakdown";
import { useLeaseAmendmentActivityBreakdown, LeaseAmendmentActivityBreakdownModal } from "./LeaseAmendmentActivityBreakdown";
import { useSecurityDepositCoverageBreakdown, SecurityDepositCoverageModal } from "./SecurityDepositCoverageBreakdown";
import SubLeaseWetLeaseExposurePanel from "./SubLeaseWetLeaseExposurePanel";
import ClaimApprovalTurnaroundSummaryTiles from "./ClaimApprovalTurnaroundSummaryTiles";
import LesseeRevenueConcentrationPanel from "./LesseeRevenueConcentrationPanel";
import AgingInvoiceBreakdownModal, { buildAgingInvoiceBreakdownRows } from "./AgingInvoiceBreakdownModal";
import ClaimRecoveryKpiPanel from "./ClaimRecoveryKpiPanel";
import CriticalOverdueObligationsList from "./CriticalOverdueObligationsList";
import { StatusPill } from "../../dashboard/components/DashboardShared";
import "../styles/analytics.scss";

const LEASE_EXPIRY_GANTT_TITLE = "Expiry Timeline (Gantt View)";
const EXPIRY_CONCENTRATION_TITLE = "Expiry Concentration by Quarter";

const GRAPH_DEFAULT_LOOKBACK_MONTHS = {
  invoiced_vs_collected: 6,
  mr_balance_vs_shop_visit_cost: 24,
};
const DEFAULT_LOOKBACK_MONTHS = 12;
const EXPIRY_CONCENTRATION_LOOKBACK_MONTHS = 24;

function isExpiryConcentrationGraph(name) {
  return typeof name === "string" && name.trim().toLowerCase() === EXPIRY_CONCENTRATION_TITLE.toLowerCase();
}

function isMrBalanceVsShopVisitCostGraph(uuid) {
  return uuid === "mr_balance_vs_shop_visit_cost";
}

function isClaimRecoveryRatioGraph(uuid) {
  return uuid === "claim_recovery_ratio";
}

function isShopVisitFrequencyByComponentGraph(uuid) {
  return uuid === "shop_visit_frequency_by_component";
}

function isFleetCashflowForecastGraph(uuid) {
  return uuid === "fleet_cashflow_forecast";
}

function isCurrencyExposureGraph(uuid) {
  return uuid === "currency_exposure";
}

function isMetaPeriodGraph(uuid) {
  return (
    isMrBalanceVsShopVisitCostGraph(uuid) ||
    isClaimRecoveryRatioGraph(uuid) ||
    isShopVisitFrequencyByComponentGraph(uuid) ||
    isFleetCashflowForecastGraph(uuid) ||
    isCurrencyExposureGraph(uuid)
  );
}

function isPaymentDelayHistoryGraph(uuid) {
  return uuid === "payment_delay_history";
}

function isInvoiceOverdueTable(uuid) {
  return uuid === "overdue_invoice_concentration";
}

function isObligationStatusOverviewGraph(uuid) {
  return uuid === "obligation_status_overview";
}

function isGeoJurisdictionConcentrationGraph(uuid) {
  return uuid === "geo_jurisdiction_concentration";
}

function isClaimApprovalTurnaroundGraph(uuid) {
  return uuid === "claim_approval_turnaround";
}

function isSubLeaseWetLeaseExposureGraph(uuid) {
  return uuid === "sub_lease_wet_lease_exposure";
}

function isLesseeRevenueConcentrationGraph(uuid) {
  return uuid === "lessee_revenue_concentration";
}

function isInvoiceVolumeValueByTypeGraph(uuid) {
  return uuid === "invoice_volume_value_by_type";
}

function isLeaseAmendmentActivityGraph(uuid) {
  return uuid === "lease_amendment_activity";
}

function isCriticalOverdueObligationsGraph(uuid) {
  return uuid === "critical_overdue_obligations";
}

function isPartialPaymentComplianceGraph(uuid) {
  return uuid === "partial_payment_compliance";
}

function isSecurityDepositCoverageGraph(uuid) {
  return uuid === "security_deposit_coverage";
}

const GRAPH_BADGE_UUIDS = new Set(["critical_overdue_obligations", "receivables_aging"]);

function canShowGraphBadge(uuid) {
  return GRAPH_BADGE_UUIDS.has(uuid);
}

const CRITICAL_OVERDUE_OBLIGATIONS_EXPORT_COLUMNS = [
  { key: "title", label: "Obligation" },
  { key: "subtitle", label: "Details" },
  { key: "bucket", label: "Status" },
  { key: "due_date", label: "Due Date", type: "date" },
  { key: "days_overdue", label: "Days Overdue" },
];

function mapBadgeTypeToPillType(type) {
  switch (String(type || "").toLowerCase()) {
    case "danger":
    case "alert":
    case "red":
      return "red";
    case "warning":
    case "warn":
    case "amber":
      return "amber";
    case "success":
    case "ok":
    case "green":
      return "green";
    default:
      return "info";
  }
}

function monthsBetweenDates(startValue, endValue) {
  const start = new Date(startValue);
  const rawEnd = new Date(endValue);
  if (Number.isNaN(start.getTime()) || Number.isNaN(rawEnd.getTime())) return null;
  const end = new Date(rawEnd);
  end.setDate(end.getDate() + 1);
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() < start.getDate()) months -= 1;
  return months > 0 ? months : 1;
}

function formatLookbackLabel(months) {
  if (months % 12 === 0) {
    const years = months / 12;
    return `${years} year${years > 1 ? "s" : ""}`;
  }
  return `${months} month${months > 1 ? "s" : ""}`;
}

function normalizeGraphResponsePayload(payload, uuid) {
  const renderType = payload?.render_type || "echart";
  const graphValues = payload?.graph_values || {};
  return {
    title: payload?.graph_name || "",
    renderType,
    options: graphValues?.options || {},
    columns: graphValues?.columns || [],
    rows: graphValues?.rows || [],
    invoiceBreakdown: Array.isArray(graphValues?.invoice_breakdown) ? graphValues.invoice_breakdown : [],
    leaseBreakdown: Array.isArray(graphValues?.lease_breakdown) ? graphValues.lease_breakdown : [],
    obligationBreakdown: Array.isArray(graphValues?.obligation_breakdown) ? graphValues.obligation_breakdown : [],
    invoiceVolumeBreakdown: Array.isArray(graphValues?.breakdown) ? graphValues.breakdown : [],
    amendmentActivityBreakdown: Array.isArray(graphValues?.breakdown) ? graphValues.breakdown : [],
    depositBreakdown: Array.isArray(graphValues?.deposit_breakdown) ? graphValues.deposit_breakdown : [],
    exposureBreakdown: Array.isArray(graphValues?.exposure_breakdown) ? graphValues.exposure_breakdown : [],
    items: Array.isArray(graphValues?.items) ? graphValues.items : [],
    kpis: graphValues?.kpis && typeof graphValues.kpis === "object" ? graphValues.kpis : null,
    summary: graphValues?.summary && typeof graphValues.summary === "object" ? graphValues.summary : null,
    badge: payload?.meta?.badge && typeof payload.meta.badge === "object" ? payload.meta.badge : null,
    message: typeof payload?.meta?.message === "string" && payload.meta.message.trim() ? payload.meta.message : null,
    period:
      graphValues?.period?.start && graphValues?.period?.end
        ? graphValues.period
        : payload?.meta?.date_range?.date_from && payload?.meta?.date_range?.date_to
        ? { start: payload.meta.date_range.date_from, end: payload.meta.date_range.date_to }
        :
          payload?.meta?.date_range?.start_date &&
          payload?.meta?.date_range?.end_date
        ? { start: payload.meta.date_range.start_date, end: payload.meta.date_range.end_date }
        : null,
    currency: payload?.meta?.currency || null,
  };
}

function getValueByPath(obj, keyPath) {
  if (!obj || typeof obj !== "object") return undefined;
  if (!keyPath || typeof keyPath !== "string") return undefined;
  return keyPath.split(".").reduce((acc, key) => {
    if (acc == null || typeof acc !== "object") return undefined;
    return acc[key];
  }, obj);
}

function cellValueForExport(col, row) {
  const raw = getValueByPath(row, col.key);
  if (col.type === "pill" && raw && typeof raw === "object") {
    return raw.label || "-";
  }
  if (col.type === "pill") {
    return raw == null || raw === "" ? "-" : String(raw);
  }
  if (col.type === "date") {
    if (raw == null || raw === "") return "-";
    return formatDateForFallbackNote(raw);
  }
  return raw == null || raw === "" ? "-" : String(raw);
}

function formatDateForFallbackNote(dateValue) {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return String(dateValue);
  return new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatBackendDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function chartHasRenderableData(options) {
  if (!options || typeof options !== "object") return false;
  if (Object.keys(options).length === 0) return false;

  const xAxes = Array.isArray(options.xAxis) ? options.xAxis : options.xAxis != null ? [options.xAxis] : [];
  const yAxes = Array.isArray(options.yAxis) ? options.yAxis : options.yAxis != null ? [options.yAxis] : [];

  for (const ax of [...xAxes, ...yAxes]) {
    if (Array.isArray(ax?.data) && ax.data.length === 0) {
      return false;
    }
  }

  const series = options.series;
  if (!Array.isArray(series) || series.length === 0) {
    return false;
  }

  return series.some(
    (s) =>
      s &&
      typeof s === "object" &&
      Array.isArray(s.data) &&
      s.data.length > 0,
  );
}

const DEFAULT_CHART_HEIGHT_PX = 420;
const CLAIM_RECOVERY_CHART_HEIGHT_PX = 500;
const GANTT_ROW_BAND_PX = 34;
const GANTT_CHART_HEIGHT_EXTRA_BUFFER = 24;
const TABLE_HEADER_HEIGHT_PX = 40;
const TABLE_ROW_HEIGHT_PX = 36;
const TABLE_MIN_HEIGHT_PX = 260;
const TABLE_TITLE_BAR_HEIGHT_PX = 40;

function computeTableHeightPx(rowCount, maxHeightPx) {
  const estimated = TABLE_HEADER_HEIGHT_PX + rowCount * TABLE_ROW_HEIGHT_PX;
  return Math.min(maxHeightPx, Math.max(TABLE_MIN_HEIGHT_PX, estimated));
}
const METRIC_OPTIONS = [ { value: "fh", label: "FH" }, { value: "fc", label: "FC" }, ];
const ASSET_TYPE_OPTIONS = [ { value: 1, label: "Aircraft" }, { value: 2, label: "Engine" }, { value: 3, label: "APU" }, { value: 4, label: "Landing Gear" }, { value: 5, label: "Propeller" }, ];
const CHART_TYPE_TOGGLE_OPTIONS = [ { type: "bar", label: "Bar" }, { type: "line", label: "Line" }, ];
const CHART_TYPE_ACTIVE_COLOR = "#564B8D";
const CHART_TYPE_INACTIVE_COLOR = "#F0B93D";

function ChartTypeToggle({ value, onChange }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2.5, py: 0.5 }}>
      {CHART_TYPE_TOGGLE_OPTIONS.map((opt) => {
        const active = value === opt.type;
        const color = active ? CHART_TYPE_ACTIVE_COLOR : CHART_TYPE_INACTIVE_COLOR;
        return (
          <Box key={opt.type} onClick={() => onChange(opt.type)} sx={{ display: "flex", alignItems: "center", gap: 0.75, cursor: "pointer", userSelect: "none", px: 1, py: 0.4, borderRadius: "14px", backgroundColor: active ? "rgba(86,75,141,0.08)" : "transparent", }} >
            {opt.type === "bar" ? (
              <Box sx={{ width: 22, height: 11, borderRadius: "6px", backgroundColor: color }} />
            ) : (
              <svg width="22" height="11" viewBox="0 0 22 11">
                <line x1="0" y1="5.5" x2="22" y2="5.5" stroke={color} strokeWidth="2" strokeDasharray="3,3" />
                <circle cx="11" cy="5.5" r="3.5" fill={color} />
              </svg>
            )}
            <Box component="span" sx={{ fontSize: 13, color, fontWeight: active ? 600 : 400 }}>
              {opt.label}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

const SUMMARY_TILE_SX = {
  flex: 1,
  border: "1px solid #E8EBF0",
  borderRadius: "10px",
  padding: "14px 16px",
  backgroundColor: "#FAFAFC",
};

function formatCompactCurrency(value, currency) {
  const amount = typeof value === "number" ? value : parseFloat(value);
  if (!Number.isFinite(amount)) return "-";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency || "USD",
      notation: "compact",
      minimumFractionDigits: 1,
      maximumFractionDigits: 1,
    }).format(amount);
  } catch {
    return amount.toFixed(1);
  }
}

function formatWholePercent(value) {
  const pct = typeof value === "number" ? value : parseFloat(value);
  return Number.isFinite(pct) ? `${Math.round(pct)}%` : "-";
}

function GeoJurisdictionSummaryTiles({ summary, currency }) {
  if (!summary) return null;
  const jurisdictionCount = Number.isFinite(summary?.jurisdiction_count) ? summary.jurisdiction_count : "-";
  const tiles = [
    { value: jurisdictionCount, label: "Jurisdictions" },
    { value: formatWholePercent(summary?.largest_exposure_pct), label: "Largest exposure" },
    { value: formatCompactCurrency(summary?.total_revenue_exposure, currency), label: "12M revenue" },
  ];
  return (
    <Box sx={{ display: "flex", gap: 1.5, mb: 1.5 }}>
      {tiles.map((tile) => (
        <Box key={tile.label} sx={SUMMARY_TILE_SX}>
          <Box sx={{ fontSize: 22, fontWeight: 700, color: "#1B2A4A", lineHeight: 1.2 }}>{tile.value}</Box>
          <Box sx={{ fontSize: 13, color: "#8E97A8", mt: 0.5 }}>{tile.label}</Box>
        </Box>
      ))}
    </Box>
  );
}

function GraphDropdownFilter({ options, value, onChange, width }) {
  return (
    <Autocomplete
      size="small"
      disableClearable
      blurOnSelect
      options={options}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      value={options.find((o) => o.value === value) || options[0]}
      onChange={onChange}
      sx={{
        width,
        "& .MuiOutlinedInput-root": {
          height: 30,
          fontSize: "12px",
          color: "primary.main",
          "& fieldset": { borderColor: "primary.main" },
          "&:hover fieldset": { borderColor: "primary.main" },
          "&.Mui-focused fieldset": { borderColor: "primary.main" },
        },
        "& .MuiAutocomplete-popupIndicator": {
          color: "primary.main",
        },
      }}
      renderInput={(params) => (
        <TextField {...params} variant="outlined" inputProps={{ ...params.inputProps, readOnly: true }} />
      )}
    />
  );
}

function getLeaseExpiryGanttMinHeightPx(options) {
  if (!options || options.title?.text !== LEASE_EXPIRY_GANTT_TITLE) return null;
  const grid = options.grid || {};
  const top = typeof grid.top === "number" ? grid.top : 52;
  const bottom = typeof grid.bottom === "number" ? grid.bottom : 68;
  const yAxis = options.yAxis;
  const axes = Array.isArray(yAxis) ? yAxis : yAxis ? [yAxis] : [];
  let rowCount = 0;
  for (const ax of axes) {
    if (Array.isArray(ax?.data) && ax.data.length) {
      rowCount = ax.data.length;
      break;
    }
  }
  if (rowCount === 0) return null;
  return top + bottom + rowCount * GANTT_ROW_BAND_PX + GANTT_CHART_HEIGHT_EXTRA_BUFFER;
}


const GANTT_SYMBOL_WIDTH_CAP_PX = 132;

function normalizeLeaseExpiryGanttOptions(options) {
  if (!options || typeof options !== "object") return options;
  if (options.title?.text !== LEASE_EXPIRY_GANTT_TITLE) return options;

  let next;
  try {
    next = JSON.parse(JSON.stringify(options));
  } catch {
    return options;
  }

  let maxSymbolW = 0;
  for (const s of next.series || []) {
    if (s.type !== "scatter" || !Array.isArray(s.data)) continue;
    for (const d of s.data) {
      if (Array.isArray(d.symbolSize) && typeof d.symbolSize[0] === "number") {
        maxSymbolW = Math.max(maxSymbolW, d.symbolSize[0]);
      }
    }
  }
  const effectiveBarW = Math.min(maxSymbolW || GANTT_SYMBOL_WIDTH_CAP_PX, GANTT_SYMBOL_WIDTH_CAP_PX);
  const minRightForHalfBar =
    Math.ceil(effectiveBarW / 2) + 72;

  next.grid = { ...next.grid };
  const gl = typeof next.grid.left === "number" ? next.grid.left : 205;
  const gr = typeof next.grid.right === "number" ? next.grid.right : 112;
  next.grid.left = Math.max(gl, 205);
  next.grid.right = Math.max(gr, minRightForHalfBar);

  const yAxes = Array.isArray(next.yAxis) ? next.yAxis : next.yAxis ? [next.yAxis] : [];
  let rowCount = 0;
  for (const ax of yAxes) {
    if (Array.isArray(ax?.data) && ax.data.length) {
      rowCount = ax.data.length;
      break;
    }
  }
  if (rowCount > 0) {
    next.grid.height =
      rowCount * GANTT_ROW_BAND_PX + GANTT_CHART_HEIGHT_EXTRA_BUFFER;
  }

  for (const ax of yAxes) {
    if (!ax?.axisLabel) continue;
    ax.axisLabel = {
      ...ax.axisLabel,
      width: 108,
      overflow: "truncate",
      margin: 20,
    };
  }

  const xAxes = Array.isArray(next.xAxis) ? next.xAxis : next.xAxis ? [next.xAxis] : [];
  for (const ax of xAxes) {
    if (!ax?.axisLabel) continue;
    ax.axisLabel = { ...ax.axisLabel, margin: 8 };
    if (ax.type === "category" || ax.type == null) {
      const bg = ax.boundaryGap;
      const hasEndPadding =
        Array.isArray(bg) && bg.length >= 2 && bg[1] != null && bg[1] !== "";
      if (!hasEndPadding) {
        ax.boundaryGap = ["5%", "16%"];
      }
    }
  }

  for (const s of next.series || []) {
    if (s.type !== "scatter" || !Array.isArray(s.data)) continue;
    s.clip = false;
    for (const d of s.data) {
      if (!Array.isArray(d.symbolSize)) continue;
      const rawW = d.symbolSize[0];
      const cappedW = Math.min(rawW, GANTT_SYMBOL_WIDTH_CAP_PX);
      d.symbolSize = [cappedW, d.symbolSize[1] ?? 24];
      if (!d?.label) continue;
      const symW = d.symbolSize[0];
      const labelWidth = Math.max(40, symW - 10);
      d.label = {
        ...d.label,
        padding: [2, 5, 2, 5],
        overflow: "truncate",
        width: labelWidth,
      };
    }
  }

  return next;
}

const GraphCard = ({ graph, variant = "analytics", pinnedEntry, onUnpin, onPinnedChange, analyticsFilterMenu, }) => {
  const { enqueueSnackbar } = useSnackbar();

  const handleItemClicked = (page_type) => {
    trackActivity('Item Clicked', { page_title: 'Analytics', page_type: page_type })
  }
  const [options, setOptions] = useState(null);
  const [graphTitle, setGraphTitle] = useState(graph?.graph_name || "");
  const [renderType, setRenderType] = useState("echart");
  const [tableColumns, setTableColumns] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [invoiceBreakdown, setInvoiceBreakdown] = useState([]);
  const [leaseBreakdown, setLeaseBreakdown] = useState([]);
  const [obligationBreakdown, setObligationBreakdown] = useState([]);
  const [invoiceVolumeBreakdown, setInvoiceVolumeBreakdown] = useState([]);
  const [amendmentActivityBreakdown, setAmendmentActivityBreakdown] = useState([]);
  const [depositBreakdown, setDepositBreakdown] = useState([]);
  const [exposureBreakdown, setExposureBreakdown] = useState([]);
  const [chartItems, setChartItems] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [graphSummary, setGraphSummary] = useState(null);
  const [graphBadge, setGraphBadge] = useState(null);
  const [graphMessage, setGraphMessage] = useState(null);
  const [graphPeriod, setGraphPeriod] = useState(null);
  const [graphMetaCurrency, setGraphMetaCurrency] = useState(null);
  const [selectedAgingBand, setSelectedAgingBand] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pinActionLoading, setPinActionLoading] = useState(false);
  const [graphFilter, setGraphFilter] = useState({});
  const [metricValue, setMetricValue] = useState("fh");
  const assetTypeOptions = useMemo(
    () => (hanwahaInstance ? ASSET_TYPE_OPTIONS.filter((opt) => opt.value === 2) : ASSET_TYPE_OPTIONS),
    [],
  );
  const [assetTypeValue, setAssetTypeValue] = useState(hanwahaInstance ? 2 : 1);
  const [chartTypeValue, setChartTypeValue] = useState("bar");
  const [TABLE_MAX_HEIGHT_PX,set_TABLE_MAX_HEIGHT_PX] = useState(Math.min(530,window.innerHeight-250));
  const chartRef = useRef(null);

  const isPaymentDelayHistory = isPaymentDelayHistoryGraph(graph?.uuid);
  const isInvoiceOverdueTablee = isInvoiceOverdueTable(graph?.uuid)
  const isCriticalOverdueObligations = isCriticalOverdueObligationsGraph(graph?.uuid);
  const isObligationStatusOverview = isObligationStatusOverviewGraph(graph?.uuid);

  const {
    rows: displayedBreakdownRows,
    canRender: canRenderBreakdown,
    canFilterByPeriod: canFilterBreakdownByPeriod,
    chartEvents,
    modalOpen: breakdownModalOpen,
    closeModal: closeBreakdownModal,
    titleSuffix: breakdownTitleSuffix,
    reset: resetInvoiceBreakdownFilters,
  } = useInvoiceBreakdown({ invoiceBreakdown: isPaymentDelayHistory || isInvoiceOverdueTablee || isInvoiceVolumeValueByTypeGraph(graph?.uuid)  ? [] : invoiceBreakdown, chartItems, onTrack: handleItemClicked });

  const {
    rows: displayedLeaseBreakdownRows,
    canRender: canRenderLeaseBreakdown,
    canFilterByQuarter: canFilterLeaseBreakdownByQuarter,
    chartEvents: leaseBreakdownChartEvents,
    modalOpen: leaseBreakdownModalOpen,
    closeModal: closeLeaseBreakdownModal,
    titleSuffix: leaseBreakdownTitleSuffix,
    reset: resetLeaseBreakdownFilters,
  } = useLeaseExpiryBreakdown({ leaseBreakdown, chartItems, onTrack: handleItemClicked });

  const {
    rows: displayedPaymentDelayRows,
    canRender: canRenderPaymentDelayBreakdown,
    canFilterByPeriod: canFilterPaymentDelayByPeriod,
    chartEvents: paymentDelayChartEvents,
    modalOpen: paymentDelayModalOpen,
    closeModal: closePaymentDelayModal,
    titleSuffix: paymentDelayTitleSuffix,
    reset: resetPaymentDelayFilters,
  } = usePaymentDelayBreakdown({ invoiceBreakdown: isPaymentDelayHistory ? invoiceBreakdown : [], chartItems, onTrack: handleItemClicked });

  const {
    rows: displayedOverdueConcentrationRows,
    canRender: canRenderOverdueConcentration,
    chartEvents: overdueConcentrationChartEvents,
    modalOpen: overdueConcentrationModalOpen,
    closeModal: closeOverdueConcentrationModal,
    titleSuffix: overdueConcentrationTitleSuffix,
    reset: resetOverdueConcentrationFilters,
  } = useOverdueInvoiceConcentrationBreakdown({ invoiceBreakdown: isInvoiceOverdueTablee ? invoiceBreakdown : [], onTrack: handleItemClicked });

  const {
    rows: displayedObligationStatusRows,
    canRender: canRenderObligationStatusBreakdown,
    chartEvents: obligationStatusChartEvents,
    modalOpen: obligationStatusModalOpen,
    closeModal: closeObligationStatusModal,
    titleSuffix: obligationStatusTitleSuffix,
    reset: resetObligationStatusFilters,
  } = useObligationStatusBreakdown({ obligationBreakdown: isObligationStatusOverview ? obligationBreakdown : [], onTrack: handleItemClicked });

  const {
    rows: displayedInvoiceVolumeRows,
    canRender: canRenderInvoiceVolumeBreakdown,
    chartEvents: invoiceVolumeChartEvents,
    modalOpen: invoiceVolumeModalOpen,
    closeModal: closeInvoiceVolumeModal,
    titleSuffix: invoiceVolumeTitleSuffix,
    reset: resetInvoiceVolumeFilters,
  } = useInvoiceVolumeValueByTypeBreakdown({
    breakdown: isInvoiceVolumeValueByTypeGraph(graph?.uuid) ? invoiceVolumeBreakdown : [],
    onTrack: handleItemClicked,
  });

  const {
    rows: displayedAmendmentActivityRows,
    canRender: canRenderAmendmentActivityBreakdown,
    chartEvents: amendmentActivityChartEvents,
    modalOpen: amendmentActivityModalOpen,
    closeModal: closeAmendmentActivityModal,
    titleSuffix: amendmentActivityTitleSuffix,
    reset: resetAmendmentActivityFilters,
  } = useLeaseAmendmentActivityBreakdown({
    breakdown: isLeaseAmendmentActivityGraph(graph?.uuid) ? amendmentActivityBreakdown : [],
    onTrack: handleItemClicked,
  });

  const isSecurityDepositCoverage = isSecurityDepositCoverageGraph(graph?.uuid);
  const {
    rows: displayedSecurityDepositRows,
    isExposure: securityDepositIsExposure,
    canRender: canRenderSecurityDepositBreakdown,
    chartEvents: securityDepositChartEvents,
    modalOpen: securityDepositModalOpen,
    closeModal: closeSecurityDepositModal,
    titleSuffix: securityDepositTitleSuffix,
    reset: resetSecurityDepositFilters,
  } = useSecurityDepositCoverageBreakdown({
    depositBreakdown: isSecurityDepositCoverage ? depositBreakdown : [],
    exposureBreakdown: isSecurityDepositCoverage ? exposureBreakdown : [],
    onTrack: handleItemClicked,
  });

  const filterKeysFromApi = useMemo(() => parseGraphFilterKeys(graph?.query_json), [graph?.query_json], );

  const baseFilterMenuForGraph = useMemo(() => pickFilterMenuForKeys(analyticsFilterMenu, filterKeysFromApi), [analyticsFilterMenu, filterKeysFromApi], );

  // Scoped to this graph card's own filter drawer: narrows `event_type` options to the
  // asset selected in THIS drawer, without touching the page-wide `analyticsFilterMenu`
  // shared by every other graph card.
  const [scopedEventTypeOptions, setScopedEventTypeOptions] = useState(null);

  const fetchScopedEventTypeOptions = useCallback((assetTypeName, assetId) => {
    globalGetService(getMaintenanceChecksApi(assetTypeName, assetId)).then((response) => {
      if (checkApiStatus(response)) {
        setScopedEventTypeOptions(extractMaintenanceEvents(response?.data?.data));
      }
    });
  }, []);

  const handleGraphAssetChange = useCallback(
    (assetId) => {
      const selectedAsset = assetId ? (baseFilterMenuForGraph.asset?.options || []).find((item) => item.id === assetId) : null;
      if (!selectedAsset) {
        setScopedEventTypeOptions(null);
        return;
      }
      fetchScopedEventTypeOptions(assetTypeNameById[selectedAsset.asset_type], selectedAsset.id);
    },
    [baseFilterMenuForGraph, fetchScopedEventTypeOptions],
  );

  // When this graph exposes the standalone Asset Type dropdown (rendered outside the
  // drawer), scope the drawer's own Asset options down to that asset type.
  const hasAssetTypeFilterCapability = filterKeysFromApi.includes("asset_type");

  const filterMenuForGraph = useMemo(() => {
    if (!baseFilterMenuForGraph.asset && !baseFilterMenuForGraph.event_type) return baseFilterMenuForGraph;
    const next = { ...baseFilterMenuForGraph };
    if (baseFilterMenuForGraph.asset && hasAssetTypeFilterCapability) {
      next.asset = {
        ...baseFilterMenuForGraph.asset,
        options: (baseFilterMenuForGraph.asset.options || []).filter((item) => item.asset_type === assetTypeValue),
      };
    }
    if (baseFilterMenuForGraph.event_type && scopedEventTypeOptions != null) {
      next.event_type = { ...baseFilterMenuForGraph.event_type, options: scopedEventTypeOptions };
    }
    if (baseFilterMenuForGraph.asset && baseFilterMenuForGraph.event_type) {
      next.onDependencyChange = (keyParam, value) => {
        if (keyParam === "asset") handleGraphAssetChange(value);
      };
    }
    return next;
  }, [baseFilterMenuForGraph, hasAssetTypeFilterCapability, assetTypeValue, scopedEventTypeOptions, handleGraphAssetChange]);

  const filterCapabilityKey = useMemo(() => filterKeysFromApi.join("|"), [filterKeysFromApi], );

  const isGraphFiltersVariant = variant === "analytics" || variant === "dashboard";

  const showGraphFilters = isGraphFiltersVariant && analyticsFilterMenu && Object.keys(filterMenuForGraph).length > 0;
  const graphQuery = useMemo(() => {
    const hasFilterMenu = isGraphFiltersVariant && filterCapabilityKey.length > 0;
    const fq = hasFilterMenu ? cleanFilterQuery(graphFilter) : {};
    if (hasFilterMenu) {
      if (filterKeysFromApi.includes("metric")) {
        fq.metric = metricValue;
      }
      if (filterKeysFromApi.includes("asset_type") && assetTypeValue != null) {
        fq.asset_type = assetTypeValue;
      }
      if (filterKeysFromApi.includes("chart_type")) {
        fq.chart_type = chartTypeValue;
      }
    }
    return Object.keys(fq).length ? fq : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGraphFiltersVariant, filterCapabilityKey, graphFilter, filterKeysFromApi, metricValue, assetTypeValue, chartTypeValue, graph?.uuid]);

  const graphFetchKey = useMemo(() => {
    if (!graph?.uuid) return null;
    return `${graph.uuid}|${variant}|${filterCapabilityKey}|${graphQuery ? JSON.stringify(graphQuery) : ""}`;
  }, [graph?.uuid, variant, filterCapabilityKey, graphQuery]);

  const onApplyGraphFilter = useCallback((query) => {
    setGraphFilter(query || {});
    handleItemClicked?.(`Graph Filter Applied: ${graphTitle || graph?.graph_name || "Graph"}`);
  }, [handleItemClicked, graphTitle, graph?.graph_name]);

  useEffect(() => {
    const handleResize = () => {
      set_TABLE_MAX_HEIGHT_PX(Math.min(530, window.innerHeight - 250));
    };

    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    setGraphFilter({});
    setMetricValue("fh");
    setAssetTypeValue(hanwahaInstance ? 2 : 1);
    setChartTypeValue("bar");
    setScopedEventTypeOptions(null);
  }, [graph?.uuid]);

  const isPinned = graph?.user_pinned?.is_pinned ?? graph?.is_pinned ?? false;

  const updatePin = (nextPinned) => {
    if (graph.id == null || pinActionLoading) return;
    handleItemClicked?.(nextPinned ? "Pin to Dashboard Button" : "Unpin Button");
    setPinActionLoading(true);
    globalPostService(nextPinned ? pinGraphApi() : unpinGraphApi(), { graph: graph.id })
      .then((response) => {
        if (checkApiStatus(response)) {
          enqueueSnackbar(response?.data?.message, { variant: "success", anchorOrigin: { vertical: "top", horizontal: "right", }, });
          if (variant === "dashboard") {
            onUnpin?.(pinnedEntry?.id);
          } else {
            onPinnedChange?.(graph.id, nextPinned);
          }
        }
      })
      .catch(() => { })
      .finally(() => {
        setPinActionLoading(false);
      });
  };

  const togglePin = () => updatePin(!isPinned);
  const handleUnpinFromDashboard = () => updatePin(false);

  useEffect(() => {
    const resetGraphState = () => {
      setOptions(null);
      setGraphTitle(graph?.graph_name || "");
      setRenderType("echart");
      setTableColumns([]);
      setTableRows([]);
      setInvoiceBreakdown([]);
      setLeaseBreakdown([]);
      setObligationBreakdown([]);
      setInvoiceVolumeBreakdown([]);
      setAmendmentActivityBreakdown([]);
      setDepositBreakdown([]);
      setExposureBreakdown([]);
      setChartItems([]);
      setKpis(null);
      setGraphSummary(null);
      setGraphBadge(null);
      setGraphMessage(null);
      setGraphPeriod(null);
      setGraphMetaCurrency(null);
      setSelectedAgingBand(null);
      resetInvoiceBreakdownFilters();
      resetLeaseBreakdownFilters();
      resetPaymentDelayFilters();
      resetOverdueConcentrationFilters();
      resetObligationStatusFilters();
      resetInvoiceVolumeFilters();
      resetAmendmentActivityFilters();
      resetSecurityDepositFilters();
    };

    if (!graphFetchKey || !graph?.uuid) {
      resetGraphState();
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    globalGetService(getGraphOptionsApi(graph.uuid, graphQuery))
      .then((response) => {
        if (cancelled) return;
        if (checkApiStatus(response)) {
          const payload = response?.data?.data || {};
          const normalized = normalizeGraphResponsePayload(payload, graph.uuid);
          setGraphTitle(normalized.title || graph?.graph_name || "");
          setRenderType(normalized.renderType);
          setOptions(normalized.options);
          if (isCriticalOverdueObligationsGraph(graph.uuid)) {
            setTableColumns(CRITICAL_OVERDUE_OBLIGATIONS_EXPORT_COLUMNS);
            setTableRows(normalized.items);
          } else {
            setTableColumns(normalized.columns);
            setTableRows(normalized.rows);
          }
          setInvoiceBreakdown(normalized.invoiceBreakdown);
          setLeaseBreakdown(normalized.leaseBreakdown);
          setObligationBreakdown(normalized.obligationBreakdown);
          setInvoiceVolumeBreakdown(normalized.invoiceVolumeBreakdown);
          setAmendmentActivityBreakdown(normalized.amendmentActivityBreakdown);
          setDepositBreakdown(normalized.depositBreakdown);
          setExposureBreakdown(normalized.exposureBreakdown);
          setChartItems(normalized.items);
          setKpis(normalized.kpis);
          setGraphSummary(normalized.summary);
          setGraphBadge(normalized.badge);
          setGraphMessage(normalized.message);
          setGraphPeriod(normalized.period);
          setGraphMetaCurrency(normalized.currency);
          setSelectedAgingBand(null);
          resetInvoiceBreakdownFilters();
          resetLeaseBreakdownFilters();
          resetPaymentDelayFilters();
          resetObligationStatusFilters();
          resetInvoiceVolumeFilters();
          resetAmendmentActivityFilters();
          resetSecurityDepositFilters();
        } else {
          resetGraphState();
        }
      })
      .catch(() => {
        if (!cancelled) resetGraphState();
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphFetchKey, graph?.graph_name]);

  const displayOptions = useMemo(() => {
    const normalized = normalizeLeaseExpiryGanttOptions(options);
    if (!normalized || typeof normalized !== "object") {
      return normalized;
    }
    const { toolbox, ...rest } = normalized;
    if (rest.title) {
      rest.title = { ...rest.title, show: false };
    }
    return rest;
  }, [options]);

  const chartHeightPx = useMemo(() => {
    if (isClaimRecoveryRatioGraph(graph?.uuid)) return CLAIM_RECOVERY_CHART_HEIGHT_PX;
    if (!options || typeof options !== "object") return DEFAULT_CHART_HEIGHT_PX;

    // After normalize, grid.height pins the plot; outer height must be top + height + bottom (matches ECharts layout).
    if (
      displayOptions?.title?.text === LEASE_EXPIRY_GANTT_TITLE &&
      typeof displayOptions?.grid?.height === "number"
    ) {
      const g = displayOptions.grid;
      const top = typeof g.top === "number" ? g.top : 52;
      const bottom = typeof g.bottom === "number" ? g.bottom : 68;
      const computed = top + g.height + bottom;
      const suggested = options._suggestedChartHeightPx;
      if (typeof suggested === "number" && suggested > 0) {
        return Math.max(suggested, computed);
      }
      return computed;
    }

    const ganttMin = getLeaseExpiryGanttMinHeightPx(options);
    const suggested = options._suggestedChartHeightPx;
    if (typeof suggested === "number" && suggested > 0) {
      if (ganttMin != null) return Math.max(suggested, ganttMin);
      return suggested;
    }
    if (ganttMin != null) return ganttMin;
    const yAxis = options.yAxis;
    const axes = Array.isArray(yAxis) ? yAxis : yAxis ? [yAxis] : [];
    let rowCount = 0;
    for (const ax of axes) {
      if (Array.isArray(ax?.data) && ax.data.length) {
        rowCount = ax.data.length;
        break;
      }
    }
    if (rowCount > 0) {
      const grid = options.grid || {};
      const top = typeof grid.top === "number" ? grid.top : 52;
      const bottom = typeof grid.bottom === "number" ? grid.bottom : 68;
      return top + bottom + rowCount * 36;
    }
    return DEFAULT_CHART_HEIGHT_PX;
  }, [options, displayOptions, graph?.uuid]);

  const canRenderChart = useMemo(
    () => chartHasRenderableData(displayOptions),
    [displayOptions],
  );
  const canRenderTable = Array.isArray(tableColumns) && tableColumns.length > 0;

  const canRenderClaimRecoveryKpis =
    isClaimRecoveryRatioGraph(graph?.uuid) && renderType !== "table" && Boolean(kpis);

  const canRenderSubLeaseWetLeaseExposure =
    isSubLeaseWetLeaseExposureGraph(graph?.uuid) && Boolean(graphSummary);

  const canRenderLesseeRevenueConcentration =
    isLesseeRevenueConcentrationGraph(graph?.uuid) && Boolean(graphSummary);

  const hasDateFilterCapability =
    !isLesseeRevenueConcentrationGraph(graph?.uuid) &&
    (filterKeysFromApi.includes("start_date") ||
      filterKeysFromApi.includes("end_date"));
  const hasMetricFilterCapability = filterKeysFromApi.includes("metric");
  const hasCurrencyFilterCapability = filterKeysFromApi.includes("currency");
  const defaultCurrency = hasCurrencyFilterCapability ? graphMetaCurrency || null : null;
  const hasAsOfDateFilterCapability = filterKeysFromApi.includes("as_of_date");
  const defaultAsOfDate = hasAsOfDateFilterCapability ? formatBackendDate(new Date()) : null;
  const onMetricChange = useCallback(
    (_, option) => {
      const value = option?.value || "fh";
      setMetricValue(value);
      handleItemClicked?.(`Graph Metric Changed: ${value}`);
    },
    [handleItemClicked],
  );
  const onAssetTypeChange = useCallback(
    (_, option) => {
      const value = option?.value ?? 1;
      setAssetTypeValue(value);
      // Previously selected asset (and its dependent event type) may not belong to the new asset type.
      setGraphFilter((prev) => {
        if (!prev?.asset && !prev?.event_type) return prev;
        const next = { ...prev };
        delete next.asset;
        delete next.event_type;
        return next;
      });
      setScopedEventTypeOptions(null);
      handleItemClicked?.(`Graph Asset Type Changed: ${option?.label || "Aircraft"}`);
    },
    [handleItemClicked],
  );
  const onChartTypeToggle = useCallback(
    (type) => {
      setChartTypeValue(type);
      handleItemClicked?.(`Graph Chart Type Changed: ${type}`);
    },
    [handleItemClicked],
  );
  const defaultDateRange = useMemo(() => {
    if (!hasDateFilterCapability && !isMetaPeriodGraph(graph?.uuid)) return null;

    if (isExpiryConcentrationGraph(graphTitle) || isExpiryConcentrationGraph(graph?.graph_name)) {
      const now = new Date();
      const end = new Date(now);
      end.setMonth(now.getMonth() + EXPIRY_CONCENTRATION_LOOKBACK_MONTHS);
      return {
        start: formatBackendDate(now),
        end: formatBackendDate(end),
        startLabel: formatDateForFallbackNote(now),
        endLabel: formatDateForFallbackNote(end),
        lookbackLabel: formatLookbackLabel(EXPIRY_CONCENTRATION_LOOKBACK_MONTHS),
        lookbackMonths: EXPIRY_CONCENTRATION_LOOKBACK_MONTHS,
        isForwardLooking: true,
      };
    }

    const months = GRAPH_DEFAULT_LOOKBACK_MONTHS[graph?.uuid] || DEFAULT_LOOKBACK_MONTHS;

    if (isMetaPeriodGraph(graph?.uuid) && graphPeriod?.start && graphPeriod?.end) {
      const actualMonths = monthsBetweenDates(graphPeriod.start, graphPeriod.end) || months;
      return {
        start: graphPeriod.start,
        end: graphPeriod.end,
        startLabel: formatDateForFallbackNote(graphPeriod.start),
        endLabel: formatDateForFallbackNote(graphPeriod.end),
        lookbackLabel: formatLookbackLabel(actualMonths),
        lookbackMonths: actualMonths,
        isForwardLooking: isFleetCashflowForecastGraph(graph?.uuid),
      };
    }

    const now = new Date();
    const start = new Date(now);
    start.setMonth(now.getMonth() - months);
    return {
      start: formatBackendDate(start),
      end: formatBackendDate(now),
      startLabel: formatDateForFallbackNote(start),
      endLabel: formatDateForFallbackNote(now),
      lookbackLabel: formatLookbackLabel(months),
      lookbackMonths: months,
    };
  }, [hasDateFilterCapability, graph?.uuid, graph?.graph_name, graphTitle, graphPeriod]);

  const dateFallbackNote = useMemo(() => {
    if (!defaultDateRange) return "";
    if (isPartialPaymentComplianceGraph(graph?.uuid)) return "";
    const fq = cleanFilterQuery(graphFilter);
    const hasStartDate = Boolean(fq.start_date);
    const hasEndDate = Boolean(fq.end_date);

    if (defaultDateRange.isForwardLooking) {
      if (!hasStartDate && !hasEndDate) {
        return `Showing default next ${defaultDateRange.lookbackLabel} data (${defaultDateRange.startLabel} - ${defaultDateRange.endLabel}).`;
      }
      if (!hasStartDate && hasEndDate) {
        const enteredEndDate = new Date(fq.end_date);
        if (Number.isNaN(enteredEndDate.getTime())) return "";
        const impliedStartDate = new Date(enteredEndDate);
        impliedStartDate.setMonth(enteredEndDate.getMonth() - defaultDateRange.lookbackMonths);
        return `Start Date is missing, so it defaults to ${defaultDateRange.lookbackLabel} before end date (${formatDateForFallbackNote(impliedStartDate)}).`;
      }
      if (hasStartDate && !hasEndDate) {
        const enteredStartDate = new Date(fq.start_date);
        if (Number.isNaN(enteredStartDate.getTime())) return "";
        const impliedEndDate = new Date(enteredStartDate);
        impliedEndDate.setMonth(enteredStartDate.getMonth() + defaultDateRange.lookbackMonths);
        return `End Date is missing, so it defaults to ${defaultDateRange.lookbackLabel} after start date (${formatDateForFallbackNote(impliedEndDate)}).`;
      }
      return "";
    }

    if (!hasStartDate && !hasEndDate) {
      return `Showing default last ${defaultDateRange.lookbackLabel} data (${defaultDateRange.startLabel} - ${defaultDateRange.endLabel}).`;
    }
    if (!hasStartDate && hasEndDate) {
      const enteredEndDate = new Date(fq.end_date);
      if (Number.isNaN(enteredEndDate.getTime())) return "";
      const impliedStartDate = new Date(enteredEndDate);
      impliedStartDate.setMonth(enteredEndDate.getMonth() - defaultDateRange.lookbackMonths);
      return `Start Date is missing, so it defaults to ${defaultDateRange.lookbackLabel} before end date (${formatDateForFallbackNote(impliedStartDate)}).`;
    }
    if (hasStartDate && !hasEndDate) {
      return `End Date is missing, so it defaults to current date (${defaultDateRange.endLabel}).`;
    }
    return "";
  }, [graphFilter, defaultDateRange, graph?.uuid]);

  const filterDrawerDefaults = useMemo(() => {
    const defaults = {};
    if (defaultDateRange) {
      defaults.start_date = defaultDateRange.start;
      defaults.end_date = defaultDateRange.end;
    }
    if (defaultCurrency) {
      defaults.currency = defaultCurrency;
    }
    if (defaultAsOfDate) {
      defaults.as_of_date = defaultAsOfDate;
    }
    return Object.keys(defaults).length ? defaults : undefined;
  }, [defaultDateRange, defaultCurrency, defaultAsOfDate]);
  const canExportTable = canRenderTable && Array.isArray(tableRows) && tableRows.length > 0;
  const isExportDisabled = loading || (renderType === "table" ? !canExportTable : !canRenderChart);
  const tableHeightPx = useMemo(
    () => computeTableHeightPx(Array.isArray(tableRows) ? tableRows.length : 0, TABLE_MAX_HEIGHT_PX),
    [tableRows, TABLE_MAX_HEIGHT_PX],
  );
  const tableCardHeightPx = tableHeightPx + TABLE_TITLE_BAR_HEIGHT_PX;

  const canShowAgingBreakdownOnRowClick = renderType === "table" && Array.isArray(invoiceBreakdown) && invoiceBreakdown.length > 0;

  const handleTableRowClick = useCallback(
    (row) => {
      const bandLabel = row?.band?.label ?? row?.band;
      if (!bandLabel) return;
      setSelectedAgingBand(bandLabel);
      handleItemClicked?.(`Aging Band Clicked: ${bandLabel}`);
    },
    [handleItemClicked],
  );

  const selectedAgingBreakdownRows = useMemo( () => selectedAgingBand ? buildAgingInvoiceBreakdownRows(invoiceBreakdown.filter((item) => item?.band === selectedAgingBand)) : [],
    [invoiceBreakdown, selectedAgingBand],
  );

  const exportFileName = graphTitle || graph?.graph_name || "graph_export";

  const tableExportColumns = useMemo(
    () =>
      tableColumns.reduce((acc, col) => {
        const header = (col.label || col.key || "").toUpperCase();
        acc[header] = (row) => cellValueForExport(col, row);
        return acc;
      }, {}),
    [tableColumns],
  );

  const handleExportButtonClick = useCallback(() => {
    if (isExportDisabled) return;
    if (renderType === "table") {
      exportToExcel({ data: tableRows, fileName: exportFileName, sheetName: "Data", columns: tableExportColumns });
      handleItemClicked?.(`Table Export: Excel - ${exportFileName}`);
      return;
    }
    const instance = chartRef.current?.getEchartsInstance?.();
    if (!instance) return;
    const dataUrl = instance.getDataURL({ type: "png", pixelRatio: 2, backgroundColor: "#fff" });
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = `${exportFileName}.png`;
    link.click();
    handleItemClicked?.(`Chart Export: Image - ${exportFileName}`);
  }, [isExportDisabled, renderType, tableRows, tableExportColumns, exportFileName, handleItemClicked]);

  return (
    <Box className="graph-card graph-card--auto-height" >
      {graphTitle || graph?.graph_name ? (
        <Box className="graph-chart-title-bar" sx={{ justifyContent: "", gap: 1 }}>
          <Box className="graph-chart-title-text" title={graphTitle || graph?.graph_name}>
            {graphTitle || graph?.graph_name}
          </Box>
          {canShowGraphBadge(graph?.uuid) && graphBadge?.text ? (
            <StatusPill label={graphBadge.text} type={mapBadgeTypeToPillType(graphBadge.type)} />
          ) : null}
        </Box>
      ) : null}
      <Box className="graph-card-header" >
        <Box className="graph-card-filter-slot" >
          {showGraphFilters ? ( <FilterComponent filter={graphFilter} defaultFilter={filterDrawerDefaults} filterMenu={filterMenuForGraph} getResponseBack={onApplyGraphFilter} tooltipContent="Filter graph" /> ) : null}
        </Box>
        <Box className="graph-card-pin-slot">
          {hasMetricFilterCapability ? (
            <GraphDropdownFilter options={METRIC_OPTIONS} value={metricValue} onChange={onMetricChange} width={90} />
          ) : null}
          {hasAssetTypeFilterCapability ? (
            <GraphDropdownFilter options={assetTypeOptions} value={assetTypeValue} onChange={onAssetTypeChange} width={150} />
          ) : null}
          {!isExportDisabled ?
          <Tooltip title={renderType === "table" ? "Export as Excel" : "Export as Image"} arrow>
            <span>
              <Button className="costum-export-btn" variant="outlined" size="small" color="primary" disabled={isExportDisabled} onClick={handleExportButtonClick} >
                <SaveAltIcon sx={{ fontSize: { xs: "small", md: "medium" } }} />
                Export
              </Button>
            </span>
          </Tooltip>:null}
          {loading ? (
            <Skeleton variant="rounded" width={110} height={28} className="skeleton-rounded-6" animation="wave" />
          ) : variant === "dashboard" ? (
            <Button color="primary" variant="outlined" size="small" disabled={pinActionLoading} onClick={handleUnpinFromDashboard}
              startIcon={
                pinActionLoading ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <PushPinOutlinedIcon className="pin-icon unpin-icon" />
                )
              }> Unpin </Button>
          ) : (
            <Button color="primary" variant="contained" size="small" disabled={pinActionLoading} onClick={togglePin}
              startIcon={
                pinActionLoading ? (
                  <CircularProgress size={14} className="icon-color-inherit" />
                ) : isPinned ? (
                  <PushPinIcon className="pin-icon" />
                ) : (
                  <PushPinOutlinedIcon className="pin-icon" />
                )
              }
            >
              {isPinned ? "Pinned to Dashboard" : "Pin to Dashboard"}
            </Button>
          )}
        </Box>
      </Box>
      {dateFallbackNote ? (
        <Box className="graph-fallback-note" >
          <InfoOutlinedIcon className="graph-fallback-icon" />
          <Box component="span"> {dateFallbackNote} </Box>
        </Box>
      ) : null}
      {graphMessage ? (
        <Box className="graph-fallback-note" >
          <InfoOutlinedIcon className="graph-fallback-icon" />
          <Box component="span"> {graphMessage} </Box>
        </Box>
      ) : null}
      {filterKeysFromApi.includes("chart_type") ? (
        <ChartTypeToggle value={chartTypeValue} onChange={onChartTypeToggle} />
      ) : null}
      {isGeoJurisdictionConcentrationGraph(graph?.uuid) && graphSummary ? (
        <GeoJurisdictionSummaryTiles summary={graphSummary} currency={graphMetaCurrency} />
      ) : null}
      {isClaimApprovalTurnaroundGraph(graph?.uuid) && graphSummary ? (
        <ClaimApprovalTurnaroundSummaryTiles summary={graphSummary} />
      ) : null}

      <Box
        className="graph-chart-area"
        sx={
          (canRenderSubLeaseWetLeaseExposure || canRenderLesseeRevenueConcentration) && !loading
            ? { height: "auto", minHeight: 0 }
            : loading
              ? { height: renderType === "table" ? tableCardHeightPx : chartHeightPx }
              : renderType === "table"
                ? { maxHeight: tableCardHeightPx }
                : { height: chartHeightPx }
        }
      >
        {loading ? (
          <Skeleton variant="rounded" width="100%" height="100%" className="skeleton-rounded-8" animation="wave" />
        ) : canRenderSubLeaseWetLeaseExposure ? (
          <SubLeaseWetLeaseExposurePanel summary={graphSummary} />
        ) : canRenderLesseeRevenueConcentration ? (
          <LesseeRevenueConcentrationPanel summary={graphSummary} items={chartItems} />
        ) : renderType === "table" ? (
          isCriticalOverdueObligations ? (
            Array.isArray(chartItems) && chartItems.length > 0 ? (
              <Box className="graph-table-wrapper">
                <CriticalOverdueObligationsList items={chartItems} maxHeight={tableHeightPx} />
              </Box>
            ) : (
              <Box className="graph-empty-state">No data found</Box>
            )
          ) : canRenderTable ? (
            <Box className="graph-table-wrapper">
              <GraphTable
                columns={tableColumns}
                rows={tableRows}
                maxHeight={tableHeightPx}
                onRowClick={canShowAgingBreakdownOnRowClick ? handleTableRowClick : undefined}
              />
            </Box>
          ) : (
            <Box className="graph-empty-state">No data found</Box>
          )
        ) : canRenderChart ? (
          canRenderClaimRecoveryKpis ? (
            <ClaimRecoveryKpiPanel
              chartRef={chartRef}
              displayOptions={displayOptions}
              chartHeightPx={chartHeightPx}
              kpis={kpis}
            />
          ) : (
            <ReactECharts
              ref={chartRef}
              option={displayOptions}
              onEvents={ canRenderBreakdown ? chartEvents : canRenderLeaseBreakdown ? leaseBreakdownChartEvents : canRenderPaymentDelayBreakdown ? paymentDelayChartEvents : canRenderOverdueConcentration ? overdueConcentrationChartEvents : canRenderObligationStatusBreakdown ? obligationStatusChartEvents : canRenderInvoiceVolumeBreakdown ? invoiceVolumeChartEvents : canRenderAmendmentActivityBreakdown ? amendmentActivityChartEvents : canRenderSecurityDepositBreakdown ? securityDepositChartEvents : undefined }
              style={{ height: chartHeightPx, width: "100%", cursor: canFilterBreakdownByPeriod || canFilterLeaseBreakdownByQuarter || canFilterPaymentDelayByPeriod || canRenderOverdueConcentration || canRenderObligationStatusBreakdown || canRenderInvoiceVolumeBreakdown || canRenderAmendmentActivityBreakdown || canRenderSecurityDepositBreakdown ? "pointer" : "default", }}
              notMerge={true}
              lazyUpdate={false}
            />
          )
        ) : (
          <Box className="graph-empty-state" sx={{ height: chartHeightPx }}>No chart data</Box>
        )}
      </Box>
      {canRenderBreakdown ? (
        <InvoiceBreakdownModal
          open={breakdownModalOpen}
          onClose={closeBreakdownModal}
          rows={displayedBreakdownRows}
          titleSuffix={breakdownTitleSuffix}
        />
      ) : null}
      {canRenderLeaseBreakdown ? (
        <LeaseExpiryBreakdownModal
          open={leaseBreakdownModalOpen}
          onClose={closeLeaseBreakdownModal}
          rows={displayedLeaseBreakdownRows}
          titleSuffix={leaseBreakdownTitleSuffix}
        />
      ) : null}
      {canRenderPaymentDelayBreakdown ? (
        <PaymentDelayBreakdownModal
          open={paymentDelayModalOpen}
          onClose={closePaymentDelayModal}
          rows={displayedPaymentDelayRows}
          titleSuffix={paymentDelayTitleSuffix}
        />
      ) : null}
      {canShowAgingBreakdownOnRowClick ? (
        <AgingInvoiceBreakdownModal
          open={Boolean(selectedAgingBand)}
          onClose={() => setSelectedAgingBand(null)}
          bandLabel={selectedAgingBand}
          rows={selectedAgingBreakdownRows}
        />
      ) : null}
      {canRenderOverdueConcentration ? (
        <OverdueInvoiceConcentrationModal
          open={overdueConcentrationModalOpen}
          onClose={closeOverdueConcentrationModal}
          rows={displayedOverdueConcentrationRows}
          titleSuffix={overdueConcentrationTitleSuffix}
        />
      ) : null}
      {canRenderObligationStatusBreakdown ? (
        <ObligationStatusBreakdownModal
          open={obligationStatusModalOpen}
          onClose={closeObligationStatusModal}
          rows={displayedObligationStatusRows}
          titleSuffix={obligationStatusTitleSuffix}
        />
      ) : null}
      {canRenderInvoiceVolumeBreakdown ? (
        <InvoiceVolumeValueByTypeBreakdownModal
          open={invoiceVolumeModalOpen}
          onClose={closeInvoiceVolumeModal}
          rows={displayedInvoiceVolumeRows}
          titleSuffix={invoiceVolumeTitleSuffix}
        />
      ) : null}
      {canRenderAmendmentActivityBreakdown ? (
        <LeaseAmendmentActivityBreakdownModal
          open={amendmentActivityModalOpen}
          onClose={closeAmendmentActivityModal}
          rows={displayedAmendmentActivityRows}
          titleSuffix={amendmentActivityTitleSuffix}
        />
      ) : null}
      {canRenderSecurityDepositBreakdown ? (
        <SecurityDepositCoverageModal
          open={securityDepositModalOpen}
          onClose={closeSecurityDepositModal}
          rows={displayedSecurityDepositRows}
          isExposure={securityDepositIsExposure}
          titleSuffix={securityDepositTitleSuffix}
        />
      ) : null}
    </Box>
  );
};

export default GraphCard;
